/**
 * DateField — Apple-style date input with floating label.
 *
 * Auto-formats as MM/DD/YYYY with real-time and on-blur validation.
 */

import { useState, useId, useRef, useEffect } from 'react';
import { format, isValid, parse, parseISO } from 'date-fns';
import { ds } from '../tokens';

export interface DateFieldProps {
  label?: string;
  /** Value in ISO format (YYYY-MM-DD) */
  value: string;
  /** Callback with ISO format (YYYY-MM-DD) */
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  /** Allow future dates (default: true) */
  allowFuture?: boolean;
  /** Minimum year allowed (default: 1900) */
  minYear?: number;
  /** Maximum year allowed (default: current year + 10) */
  maxYear?: number;
  autoFocus?: boolean;
  /** Fires when internal validation state changes (non-null = error active) */
  onValidationError?: (error: string | null) => void;
  /** Show a "Does not expire" toggle below the field */
  noExpiryOption?: boolean;
  /** Whether "Does not expire" is checked */
  noExpiry?: boolean;
  /** Called when the toggle changes */
  onNoExpiryChange?: (noExpiry: boolean) => void;
}

export function DateField({
  label,
  value,
  onChange,
  onBlur,
  error: externalError,
  helperText,
  disabled = false,
  className = '',
  id: externalId,
  allowFuture = true,
  minYear = 1900,
  maxYear,
  autoFocus = false,
  onValidationError,
  noExpiryOption = false,
  noExpiry = false,
  onNoExpiryChange,
}: DateFieldProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [validationError, setValidationError] = useState('');
  const autoId = useId();
  const inputId = externalId || autoId;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value) {
      const d = parseISO(value);
      if (isValid(d)) {
        setInputValue(format(d, 'MM/dd/yyyy'));
      }
    } else {
      setInputValue('');
    }
  }, [value]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  const formatInputValue = (raw: string, previousValue: string): string => {
    const cleaned = raw.replace(/[^\d/]/g, '');
    const digits = cleaned.replace(/\D/g, '');
    const userTypedSlash = raw.endsWith('/') && !previousValue.endsWith('/');

    let formatted = '';

    if (digits.length >= 1) {
      let month = digits.slice(0, 2);
      if (parseInt(month[0]) > 1) {
        month = '0' + month[0];
      }
      formatted = month;
    }

    if ((formatted.length === 2 && userTypedSlash) || digits.length >= 3) {
      formatted += '/';
    }

    if (digits.length >= 3) {
      let day = digits.slice(2, 4);
      if (parseInt(day[0]) > 3) {
        day = '0' + day[0];
      }
      formatted += day;
    }

    if ((formatted.length === 5 && userTypedSlash) || digits.length >= 5) {
      formatted += '/';
    }

    if (digits.length >= 5) {
      formatted += digits.slice(4, 8);
    }

    return formatted;
  };

  const validateDate = (formatted: string): string | null => {
    if (formatted.length !== 10) return null;

    const parts = formatted.split('/');
    const month = parseInt(parts[0]);
    const day = parseInt(parts[1]);
    const year = parseInt(parts[2]);
    const currentYear = new Date().getFullYear();

    if (month < 1 || month > 12) {
      return 'Month must be between 1 and 12';
    }
    if (day < 1 || day > 31) {
      return 'Day must be between 1 and 31';
    }

    const effectiveMaxYear = maxYear ?? currentYear + 10;
    if (year < minYear) {
      return `Year must be ${minYear} or later`;
    }
    if (!allowFuture && year > currentYear) {
      return 'Year cannot be in the future';
    }
    if (year > effectiveMaxYear) {
      return `Year must be ${effectiveMaxYear} or earlier`;
    }

    if (month === 2 && day > 29) {
      return 'February has max 29 days';
    }
    if ([4, 6, 9, 11].includes(month) && day > 30) {
      return 'This month has max 30 days';
    }
    if (month === 2 && day === 29) {
      const isLeap = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
      if (!isLeap) return 'Not a leap year';
    }

    const parsed = parse(formatted, 'MM/dd/yyyy', new Date());
    if (!isValid(parsed)) {
      return 'Invalid date';
    }

    return null;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const formatted = formatInputValue(raw, inputValue);
    setInputValue(formatted);

    if (validationError) {
      setValidationError('');
      onValidationError?.(null);
    }

    if (formatted.length === 10) {
      const error = validateDate(formatted);
      if (error) {
        setValidationError(error);
        onValidationError?.(error);
        onChange('');
      } else {
        const parsed = parse(formatted, 'MM/dd/yyyy', new Date());
        onChange(format(parsed, 'yyyy-MM-dd'));
      }
    } else if (formatted.length === 0) {
      onChange('');
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (inputValue.length > 0 && inputValue.length < 10) {
      const err = 'Enter a complete date (MM/DD/YYYY)';
      setValidationError(err);
      onValidationError?.(err);
      onChange('');
    }
    onBlur?.();
  };

  const isFloated = isFocused || !!inputValue;
  const displayError = validationError || externalError;

  const borderColor = displayError
    ? ds.color.raw.systemRed
    : isFocused
      ? ds.color.state.focusBorder
      : ds.color.separator.field;

  const ringClass = isFocused ? ds.color.state.focusRing : '';

  return (
    <div className={className}>
      {noExpiryOption && (
        <button
          type="button"
          onClick={() => {
            const next = !noExpiry;
            onNoExpiryChange?.(next);
            if (next) {
              setInputValue('');
              setValidationError('');
              onChange('');
            }
          }}
          className={`flex items-center gap-2.5 px-1 py-1 ${noExpiry ? '' : 'mb-2'}`}
        >
          <span
            className="relative w-9 h-5 rounded-full transition-colors duration-300 flex-shrink-0"
            style={{ backgroundColor: noExpiry ? ds.color.raw.systemBlue : ds.color.raw.systemGray4 }}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-md transition-transform duration-300 ${
                noExpiry ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </span>
          <span className={[ds.type.caption1, ds.color.label.secondary].join(' ')}>
            Does not expire
          </span>
        </button>
      )}

      {!noExpiry && (
        <>
          <div
            className={[
              'relative overflow-hidden',
              ds.color.fill.primary,
              ds.radius.md,
              'border',
              borderColor,
              ringClass,
              ds.transition.fast,
            ].filter(Boolean).join(' ')}
          >
            {label && (
              <label
                htmlFor={inputId}
                className={[
                  'absolute left-3 pointer-events-none origin-top-left',
                  ds.transition.label,
                  isFloated
                    ? ['top-1.5 scale-[0.75]', ds.color.label.floated].join(' ')
                    : ['top-1/2 -translate-y-1/2', ds.type.body, ds.color.label.tertiary].join(' '),
                ].join(' ')}
              >
                {label}
              </label>
            )}

            <input
              ref={inputRef}
              id={inputId}
              type="text"
              inputMode="numeric"
              value={inputValue}
              onChange={handleChange}
              onFocus={() => setIsFocused(true)}
              onBlur={handleBlur}
              maxLength={10}
              disabled={disabled}
              className={[
                'w-full bg-transparent outline-none px-3',
                label ? 'pt-6 pb-1.5' : 'py-2.5',
                ds.type.body,
                ds.type.weight.medium,
                ds.color.label.primary,
                ds.color.label.placeholder,
                disabled ? 'opacity-50 cursor-not-allowed' : '',
              ].filter(Boolean).join(' ')}
            />

            {!inputValue && (
              <span className={ds.field.formatHint}>
                MM/DD/YYYY
              </span>
            )}
          </div>

          {displayError && (
            <p className={[ds.type.caption1, ds.color.label.destructive, 'mt-1 px-1'].join(' ')}>
              {displayError}
            </p>
          )}

          {helperText && !displayError && (
            <p className={[ds.type.caption1, ds.color.label.secondary, 'mt-1 px-1'].join(' ')}>
              {helperText}
            </p>
          )}
        </>
      )}
    </div>
  );
}
